<template>
    <div id="question" class="d-flex flex-fill">
        <div class="d-flex flex-column w-50 border-end-dashed p-4 gap-3 overflow-auto">
            <!-- begin::question -->
            <RenderQuestion :question="question" v-if="question.desc || question.image_url"></RenderQuestion>
            <!-- end::question -->

            <!-- begin::matetials -->
            <FileList :title="$t('materials')" v-if="question.materials && question.materials.length > 0" :files="question.materials"></FileList>
            <!-- end::matetials -->
        </div>
        <div class="d-flex flex-column w-50 border-end-dashed p-4 gap-3 overflow-auto">
            <!-- answer input -->
            <div class="d-flex flex-column w-100 align-items-center gap-3" v-if="question.submit_type?.includes('text_entry')">
                <h5 class="text-primary text-bold m-0 fs-5 text-center text-uppercase">{{ $t('your_answer') }}</h5>
                <div class="textarea-wrapper">
                    <textarea ref="textarea" class="w-100" name="" id="" rows="1" v-model="answer.answer" @input="resize()" @focus="resize()" :readonly="['completed', 'submitted', 'over_due'].includes(assignment.state_submissions.status)"></textarea>
                </div>
                <div class="w-100 d-flex justify-content-end">
                    <p class="words-counter">{{ $t('words_count') }}: <span class="text-primary" :class="{ limited: wordCount == question.word_limit }">{{ wordCount }}</span>/{{ question.word_limit }}</p>
                </div>
            </div>
            <!-- end answer input -->

            <!-- file upload -->
            <div class="d-flex flex-column w-100 align-items-center gap-3" v-if="question.submit_type?.includes('file_submit') && (['not_started', 'draft', 'to_do'].includes(assignment.state_submissions.status) || question.submissions.files)">
                <h5 class="text-primary text-bold m-0 fs-5 text-center text-uppercase">{{ $t('your_files') }}</h5>
                <!-- file uploader -->
                <div class="answer-file-input overflow-hidden w-75" v-if="['not_started', 'draft', 'to_do'].includes(assignment.state_submissions.status)">
                    <div class="d-flex flex-column flex-fill">
                        <div class="d-flex w-100 h-100 align-items-center justify-content-between gap-3 p-4" id="app" @dragover="dragover" @dragleave="dragleave" @drop="drop">
                            <input type="file" name="fields[assetsFieldHandle][]" id="assetsFieldHandle" class="d-none" @change="onChange" ref="file" accept=".pdf,.jpg,.jpeg,.png,.xls,.mov,.mp3,.mp4,.gif,.doc,.docx,.xls,.xlsx" />
                            <label for="assetsFieldHandle" class="d-flex flex-column align-items-center justify-content-center text-center cursor-pointer flex-fill gap-3">
                                <img src="/themes/ito/images/assignment/CloudArrowUp.png" alt="">
                                <div>{{ $t('assignment_drag_and_drop_file') }}<br />{{ $t('or') }}</div>
                                <p class="ito-btn ito-btn-primary">{{ $t('btn_choose_file') }}</p>
                                <div class="text-danger fst-italic small" v-if="errors.file">Unable to upload a file: <br>{{ errors.file }}</div>
                            </label>
                            <div class="file-upload-helper d-flex flex-column gap-3">
                                <p>{{ $t('assignment_support_format_file_upload') }}</p>
                                <div class="list-file-suppoted d-flex flex-wrap">
                                    <div class="file-suppoted" v-for="format in supported_formats" v-if="format !== 'docx' && format !== 'xlsx'">
                                        <i :class="'ito-icon-material-' + format"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- end file uploader -->

                <!-- list uploaded files -->
                <TransitionGroup tag="ul" name="list" class="w-75 d-flex flex-column gap-3 p-0 position-relative list-unstyled">
                    <li class="w-100 d-flex gap-3 align-items-center bg-white border-dashed rounded-1rem px-4 py-3" v-for="(file, index) in question.submissions.files" :key="index">
                        <i class="ito-icon ito-icon" :class="'ito-icon-material-' + file.extension"></i>
                        <div class="d-flex flex-fill flex-column justify-content-center overflow-hidden">
                            <p class="material-name text-truncate">{{ file.name }}</p>
                            <div class="progress" v-if="file.progress && file.progress < 100 && (['not_started', 'draft', 'to_do'].includes(assignment.state_submissions.status))">
                                <div class="progress-bar" role="progressbar" :aria-valuenow="file.progress" aria-valuemin="0" aria-valuemax="100" v-bind:style="{ width: file.progress + '%' }"></div>
                            </div>
                            <p class="material-value">{{ formatBytes(file.size) }}</p>
                        </div>
                        <button class="remove-file-btn" @click="removeFile(question.submissions.files.indexOf(file))" v-if="['not_started', 'draft', 'to_do'].includes(assignment.state_submissions.status)">x</button>
                    </li>
                </TransitionGroup>
                <!-- end list uploaded files -->
            </div>
            <!-- end file upload -->

            <!-- teacher feedback -->
            <div class="asm-feedback d-flex w-100 flex-column align-items-center" v-if="question.submissions.feedback != '' && assignment.state_submissions.status == 'completed'">
                <h5 class="text-primary text-bold m-0 fs-5 text-center text-uppercase">{{ $t('teacher_feedback') }}</h5>
                <p v-html="question.submissions.feedback"></p>
            </div>
            <!-- end teacher feedback -->

            <!-- begin::Answer key -->
            <FileList :title="$t('answer_key')" v-if="assignment.answer_keys && assignment.answer_keys.length > 0" :files="assignment.answer_keys"></FileList>
            <!-- end::Answer key -->
        </div>
    </div>
</template>
<script>
import axios from 'axios'
import { getCookie } from "../../models/assignment";
import FileList from './components/FileList';
import RenderQuestion from './components/RenderQuestion';
import { handleFileExtension } from "../../utils/utils";

export default {
    name: "QuestionLongAnswer",
    components: { FileList, RenderQuestion },
    props: ['question', 'assignment'],
    data() {
        return {
            answer: (this.question?.submissions?.answer && Array.isArray(this.question.submissions.answer)) ? this.question.submissions.answer[0] : { answer: undefined, is_correct: 0 },
            wordCount: 0,
            supported_formats: ['jpeg', 'png', 'gif', 'mp3', 'mp4', 'mov', 'doc', 'docx', 'xls', 'xlsx', 'pdf'],
            errors: {
                file: undefined,
            },
        }
    },

    watch: {
        answer: {
            handler(newAnswer) {
                this.truncateAnswer(newAnswer.answer);
                this.countWords();
                this.$nextTick(() => {
                    this.resize();
                });
                this.$emit('answer', this.question.id, [this.answer]);
            },
            deep: true
        },
        'question.submissions.files': {
            handler() {
                this.$emit('answer', this.question.id, [this.answer]);
            },
            deep: true
        }
    },

    mounted() {
        if (this.question.submit_type?.includes('text_entry')) {
            this.truncateAnswer(this.answer.answer);
            this.countWords();
            let self = this;
            this.$nextTick(() => {
              setTimeout(function (){
                self.resize();
              }, 100);
            });
        }
    },

    methods: {
        countWords() {
            if (this.answer.answer) {
                const words = this.answer.answer.trim().split(/\s+/).filter(word => word.length > 0);
                this.wordCount = words.length;
            }
        },

        truncateAnswer(text) {
            if (text) {
                const words = text.trim().split(/[\s\n]+/).filter(word => word.length > 0);
                if (words.length > this.question.word_limit) {
                    // Preserve newlines
                    const originalText = text.split(/(\s+|\n+)/);
                    let wordCount = 0;
                    let result = '';
                    for (let part of originalText) {
                        if (part.trim().length > 0) {
                            wordCount++;
                            if (wordCount > this.question.word_limit) break;
                        }
                        result += part;
                    }
                    this.answer.answer = result;
                } else {
                    // Prevent additional spaces or newlines if word limit is reached
                    const lastChar = text[text.length - 1];
                    if (this.wordCount === this.question.word_limit && (lastChar === ' ' || lastChar === '\n')) {
                        this.answer.answer = text.slice(0, -1);
                    }
                }
            }
        },

        resize() {
          let element = this.$refs["textarea"];
          element.style.height = "24px";
          element.style.height = element.scrollHeight + "px";
        },

        validateFile(file) {
            const file_size = (Math.round(file.size / 1024) / 1000).toFixed(2);
            const file_extension = handleFileExtension(file.name);

            if (file_size > 5) {
                this.errors.file = 'Max file size is 5MB';
                return false;
            }

            if (!this.supported_formats.includes(file_extension)) {
                this.errors.file = 'This file type is not supported';
                return false;
            }

            this.errors.file = undefined;
            return true;
        },

        async onChange() {
            const fileInput = this.$refs.file;
            if (!fileInput || !fileInput.files.length) return;

            const file = fileInput.files[0];
            if (!this.validateFile(file)) {
                return;
            }

            if (!this.question.submissions.files) {
                this.question.submissions.files = [];
            }

            const tempFile = {
                id: new Date().getUTCMilliseconds(),
                url: undefined,
                name: file.name.split('.').shift(),
                size: file.size,
                extension: handleFileExtension(file.name),
                progress: 0,
            };

            this.question.submissions.files.unshift(tempFile);

            const formdata = new FormData();
            formdata.append("assignment_id", this.assignment.id);
            formdata.append("question_id", this.question.id);
            formdata.append("file", file);

            const token = localStorage.getItem('access_token')

            try {
                const response = await axios.post(process.env.VUE_APP_API_URL + '/assignment/uploadfile', formdata, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`,
                    },
                    onUploadProgress: (progressEvent) => {
                        this.handleUploadprogress(0, parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100)));
                    }
                });

                if (response.data.code !== 0) {
                    this.question.submissions.files.shift();
                    this.errors.file = this.$t(response.data.msg);
                    return;
                }

                this.errors.file = undefined;
                const result = response.data.results;
                if (this.question.submissions.files.length > 0) {
                    this.question.submissions.files[0].id = result.id;
                    this.question.submissions.files[0].url = result.url;
                }
            } catch (error) {
                console.error(error);
                this.question.submissions.files.shift();
            } finally {
                fileInput.value = null;
                this.$forceUpdate();
            }
        },

        handleUploadprogress(index, progress) {
            if (this.question.submissions.files && this.question.submissions.files[index]) {
                this.question.submissions.files[index].progress = progress;
            }
        },

        removeFile(i) {
            this.question.submissions.files.splice(i, 1);
        },

        dragover(event) {
            event.preventDefault();
            if (!event.currentTarget.classList.contains('bg-light')) {
                event.currentTarget.classList.add('bg-light');
            }
        },

        dragleave(event) {
            event.currentTarget.classList.remove('bg-light');
        },

        drop(event) {
            event.preventDefault();
            this.$refs.file.files = event.dataTransfer.files;
            this.onChange();
            event.currentTarget.classList.remove('bg-light');
        },

        formatBytes(bytes, decimals = 2) {
            if (!+bytes) return '0 Bytes'

            const k = 1024
            const dm = decimals < 0 ? 0 : decimals
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

            const i = Math.floor(Math.log(bytes) / Math.log(k))

            return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
        },
    }
}
</script>

<style scoped>

.border-dashed {
    border: 1px dashed #CCD2E3;
}

.border-end-dashed {
    border-right: 1px dashed #EAEAEA;
}

.rounded-1rem {
    border-radius: 1rem;
}

.material-name {
    font-size: 10px;
    font-weight: 700;
    color: #0D0D0C;
}

.material-value {
    font-size: 10px;
    font-weight: 400;
    color: #0D0D0C;
}

.textarea-wrapper {
    width: 100%;
    position: relative;
    border-radius: 8px;
    box-shadow: 0 8px #EAEAEA;
    border: 1px solid #EAEAEA;
    padding: 20px;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    max-height: 300px;
    overflow: auto;

}

.textarea-wrapper textarea {
    resize: none;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 4 4"><circle cx="2" cy="2" r="1" fill="%23979797" /></svg>');
    background-position: 0 -0.9em;
    background-size: 4px 24px;
    box-sizing: border-box;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #000000;
    border: none;
}

.words-counter {
    font-size: 14px;
    font-weight: 400;
    text-align: right;
    color: #1B1B1B;
}

.words-counter span {
    font-weight: 700;
}

.words-counter span.limited {
    color: #EF564B !important;
}

.answer-file-input {
    display: flex;
    border: 1px dashed #CCD2E3;
    border-radius: 16px;
    gap: 30px;
    justify-content: space-between;
}

.file-upload-helper {
    width: 128px;
}

.file-upload-helper .list-file-suppoted {
    justify-content: center;
    align-items: center;
}

.file-upload-helper .list-file-suppoted .file-suppoted {
    flex-grow: 1;
    width: 33.333%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.file-upload-helper p {
    font-size: 12px;
    font-weight: 400;
    color: #5153FF;
    text-align: center;
}

.remove-file-btn {
    display: flex;
    width: 20px;
    height: 20px;
    background: #F04821;
    color: #ffffff;
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    border: none
}

.progress {
    height: 7px;
}

.progress-bar {
    background-color: #5153FF;
    border-radius: 20px;
}


.list-move,
.list-enter-active,
.list-leave-active {
    transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px);
}

.list-leave-active {
    position: absolute;
}
</style>